import { resolveLink } from './utils/scriptUtils';
import { ambiguousRoomChoice, navigationQuestion } from './utils/roomMatch';

// ======================================================
// TOOL HANDLING
// ======================================================
class ToolHandler {
  constructor(state, connectionManager, emitEvent, tourGuide = null) {
    this.state = state;
    this.connectionManager = connectionManager;
    this.emitEvent = emitEvent;
    this.tourGuide = tourGuide;

    this.bridge = null; // set externally by RealtimeTourGuide

    this.handlers = {
        check_tour_complete:  this.checkTourComplete.bind(this),
        on_change_scene:      this.onChangeScene.bind(this),
        on_open_info:         this.onOpenInfo.bind(this),
        get_current_location: this.getCurrentLocation.bind(this),
        get_audio_status:     this.getAudioStatus.bind(this),
        get_position:         this.getPosition.bind(this),
        on_close:             this.onClose.bind(this),
        on_change_mode:       this.onChangeMode.bind(this),
        search_knowledge_base:this.searchKnowledgeBase.bind(this),
        on_change_language:   this.onChangeLanguage.bind(this),
        rotate_view:          this.rotateView.bind(this),
    };
  }

  // ─── Dispatch ─────────────────────────────────────────────────

  static DEFERRED_TOOLS = new Set(['on_change_scene', 'rotate_view']);
  // A knowledge lookup faster than this is answered straight away; a slower
  // one gets a short spoken "one moment" line first (RealtimeEventHandler.sayFiller).
  // A line not yet spoken when the answer arrives is cancelled.
  static FILLER_DELAY_MS = 1500;
  // The transcript of the turn can land just after the tool call; wait this
  // long for it before checking a navigation request against it.
  static TRANSCRIPT_WAIT_MS = 800;

  _fillerInstructions(query) {
    const lang = this.bridge?.getLanguage() || this.state.language || 'en';
    return `You are a voice tour guide. The visitor is waiting while you look something up${query ? ` (about: "${query}")` : ''}. ` +
      `Say ONE short, natural line of 3-7 words in the visitor's language (language code: ${lang}) telling them you are on it. ` +
      'Vary the wording and never reuse a line you already said; styles like: "Let me just check that.", ' +
      '"One sec, finding the parking info.", "Bear with me a moment.", "Let\'s see…", "Looking that up for you now." ' +
      'Do NOT answer the question and do NOT say anything else.';
  }

  _waitForAudioDone() {
    const tg = this.tourGuide;
    if (!tg?.mixer?._analyser || !tg._audioActive) return Promise.resolve();
    return tg.mixer.waitForSilence();
  }

  async handleToolCall(data) {
    let args = {};
    try { args = JSON.parse(data.arguments); } catch (e) {
      console.warn('Invalid arguments JSON', data.arguments);
    }

    console.log(`[guide] tool: ${data.name}`, args);

    if (ToolHandler.DEFERRED_TOOLS.has(data.name)) {
      await this._waitForAudioDone();
    }

    let result = { success: false };
    const handler = this.handlers[data.name];
    const fillerTimer = data.name === 'search_knowledge_base' && this.events?.sayFiller
      ? setTimeout(() => this.events.sayFiller(this._fillerInstructions(args.query)), ToolHandler.FILLER_DELAY_MS)
      : null;
    if (!handler) {
      result = { success: false, message: `Unknown tool: ${data.name}` };
    } else {
      try {
        result = await handler(args, data.call_id);
      } catch (err) {
        console.error(`Error handling tool ${data.name}`, err);
        result = { success: false, message: err.message || 'Internal error' };
      }
    }
    clearTimeout(fillerTimer);
    // A "one moment" line still being generated finishes before the answer starts.
    if (this.events?.waitForFiller) await this.events.waitForFiller();

    if (this.sendResult) this.sendResult(data, result);
  }

  // ─── sendResult ───────────────────────────────────────────────

  sendResult(data, result) {
    if (!this.connectionManager?.isInit() || !this.connectionManager.dataChannel) return;

    const dc = this.connectionManager.dataChannel;
    dc.send(JSON.stringify({
      type: 'conversation.item.create',
      item: { type: 'function_call_output', call_id: data.call_id, output: JSON.stringify(result) }
    }));

    const suppressResponse =
      data.name === 'play_point_audio' ||
      // answered once the new room has loaded (Avatar.onArrive)
      !!result?.answer_on_arrival ||
      (!this.bridge?.isMuseum && data.name === 'on_change_scene' && (() => {
        const room = this.state.getRoomInfo(this.state.currentScene);
        return !!resolveLink(room?.link, this.bridge?.getLanguage() || this.state.language, this.state.tourData?.defaultLang || 'en');
      })());

    if (!suppressResponse) {
      dc.send(JSON.stringify({ type: 'response.create' }));
    }
  }

  // ─── Tool handlers ────────────────────────────────────────────

  async onChangeScene({ room_id, question }) {
    if (!room_id) return { success: false, message: 'room_id is required' };
    const transcript = await this._turnTranscript();
    const target = this.state.getRoomInfo(room_id) || this.state.findBestRoomMatch(room_id);
    const candidates = target && ambiguousRoomChoice(transcript, this.state.tourData?.scenes, target.id);
    const asked = (typeof question === 'string' && question.trim()) || navigationQuestion(transcript);
    if (candidates) {
      console.log('[guide] on_change_scene: several rooms match — asking first', candidates);
      if (asked) this.state.holdQuestion(asked);
      return {
        success: false,
        ambiguous: true,
        candidates,
        message: `Several rooms match what the visitor said: ${candidates.map(c => `"${c.room_name}"`).join(', ')}. ` +
          'Do NOT move. Ask ONE short question — which of these they mean — and nothing else. ' +
          'Then call on_change_scene with the room they pick.',
      };
    }
    const from = this.state.currentScene;
    let result;
    try {
      result = await this.bridge.navigateTo(room_id);
    } catch (err) {
      return { success: false, message: err.message };
    }
    const ask = asked || this.state.takeHeldQuestion();
    if (ask && result?.success) {
      if (String(from) !== String(result.current_room)) {
        this.state.setArrivalQuestion(ask, result.current_room);
        return { ...result, answer_on_arrival: true,
          message: 'Moving there now. Say nothing more — the question is answered automatically once the room has loaded.' };
      }
      return { ...result, message: `Already in this room. Answer the visitor's question now: "${ask}"` };
    }
    return result;
  }

  async _turnTranscript() {
    const deadline = Date.now() + ToolHandler.TRANSCRIPT_WAIT_MS;
    while (!this.state.userTranscript && Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 50));
    }
    return this.state.userTranscript || '';
  }

  onOpenInfo({ hotspot_id, type }) {
    if (!hotspot_id) return { success: false };
    let room_id = null;

    for (const scene of this.state.tourData.scenes || []) {
      if (scene.hotspots?.some(h => h.hotspotID === hotspot_id)) {
        room_id = scene.id;
        break;
      }
    }

    if (!room_id) return { success: false };
    this.state.currentScene = room_id;
    this.emitEvent('on-open-info', { room_id, hotspot_id });
    return { success: true, message: '', current_room: room_id };
  }

  getCurrentLocation() {
    const s = this.bridge.getScene();
    return { room_id: s.id, room_name: s.name, description: s.description, connected_rooms: s.connected, link: s.link };
  }

  getAudioStatus() {
    return this.bridge.getAudioStatus();
  }

  checkTourComplete() {
    return this.bridge.getTourProgress();
  }

  getPosition() {
    return this.bridge.getPosition();
  }

  onClose() {
    this.emitEvent('on-close', {});
    return { success: true };
  }

  onChangeMode({ mode }) {
    if (!mode) return { success: false, message: 'mode is required' };
    this.emitEvent('on-change-mode', { mode });
    return { success: true, message: `Mode changed to ${mode}` };
  }

  onChangeLanguage({ language }) {
    if (!language || !String(language).trim()) return { success: false, message: 'language is required' };
    let normalized = String(language).trim().toLowerCase();
    if (normalized.startsWith('en')) normalized = 'en';
    if (this.tourGuide) {
      this.tourGuide.language = normalized;
      this.state.language = normalized;
    }
    console.log(`[onChangeLanguage]: ${normalized}`);
    return { success: true, message: `Language changed to ${normalized}`, language: normalized };
  }

  async rotateView({ yaw, pitch, hfov, duration }) {
    if (yaw === undefined || yaw === null) return { success: false, message: 'yaw is required' };
    return this.bridge.rotate({ yaw, pitch, hfov, duration });
  }

  async searchKnowledgeBase({ query }) {
    if (!query?.trim()) return { success: false, message: 'query is required' };
    if (!this.tourGuide || typeof this.tourGuide.updateRAGContext !== 'function') {
      return { success: false, message: 'Knowledge base is not available.' };
    }

    // Stop any playing museum audio so it doesn't overlap with the AI's RAG answer
    if (this.tourGuide?.museumAudioPlayer?.isPlaying) {
      this.tourGuide.museumAudioPlayer.stop();
      this.tourGuide.mixer?.unmuteAI();
    }

    this.tourGuide._ragSearchInProgress = true;
    try {
      const { updated, contextText } = await this.tourGuide.updateRAGContext(query);
      if (!updated) return { success: false, message: 'No additional information found in the knowledge base for this query.' };
      return { success: true, message: 'Context found and added to conversation.' };
    } finally {
      this.tourGuide._ragSearchInProgress = false;
    }
  }
}

export default ToolHandler;
